import { useState, useEffect } from "react";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { C } from "./data";
import { Wordmark, Serif, Label } from "./ui";
import { registerConsumer, setPassword, loginConsumer, formatErr, forgotPassword, getInvitePrefill, lookupCover, lookupByEmail } from "./consumerAuth";

const qp = (k) => new URLSearchParams(window.location.search).get(k) || "";

export default function Auth({ onAuth }) {
  const inviteToken = qp("invite");
  const signupRef = qp("ref") || localStorage.getItem("rq_signup_ref") || "";
  if (qp("ref")) localStorage.setItem("rq_signup_ref", qp("ref"));
  const ci = qp("ci");
  const [mode, setMode] = useState(inviteToken || ci ? "signup" : "signin");
  const [step, setStep] = useState("identify"); // signup: identify -> details
  const [matched, setMatched] = useState(null);
  const [dup, setDup] = useState(null); // { email, cell } when a known account is detected
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [v, setV] = useState({ name: "", email: "", password: "", cell: "", id_number: "", main_member_cell: "" });
  const isSignup = mode === "signup";
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));

  const runLookup = async (cellArg) => {
    const cell = (cellArg ?? v.cell).trim();
    if (cell.replace(/\D/g, "").length < 9) return toast.error("Enter your cellphone number");
    setBusy(true);
    try {
      const r = await lookupCover(cell);
      setMatched(r.match ? r : null);
      setDup(null);
      if (r.match && r.email) setV((s) => ({ ...s, email: s.email || r.email }));
      setStep("details");
    } catch (e) {
      setMatched(null);
      setStep("details");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (ci) getInvitePrefill(ci).then((p) => {
      setV((s) => ({ ...s, cell: p.cell || s.cell, name: p.full_name || s.name }));
      if (p.cell) runLookup(p.cell);
    }).catch(() => {});
  }, [ci]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (isSignup && !matched && !(v.name || "").trim()) return toast.error("Please enter your full name");
    if (isSignup && v.password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      if (isSignup) {
        let r;
        if (inviteToken || v.main_member_cell) {
          r = await registerConsumer({ name: v.name || undefined, email: v.email.trim(), password: v.password, cell: v.cell, id_number: v.id_number, main_member_cell: v.main_member_cell || undefined, invite_token: inviteToken || undefined, contact_invite_token: ci || undefined, ref: signupRef || undefined });
        } else {
          // One reliable, tokenless path — activates a new member OR resets a returning member, then signs them in.
          r = await setPassword({ email: v.email.trim(), password: v.password, cell: v.cell, id_number: v.id_number, name: v.name || undefined, contact_invite_token: ci || undefined });
        }
        toast.success(r.reset ? "Password updated — you're in" : (inviteToken ? "You've joined your family on ResQ1" : (r.matched ? "Welcome — your ResQ1 cover is active" : "Account created")),
          !r.reset && r.user && !r.user.email ? { description: `Next time, sign in with your cell number ${r.user.cell || ""} and this password.` } : undefined);
      } else {
        await loginConsumer(v.email.trim(), v.password);
        toast.success("Signed in");
      }
      onAuth();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "";
      if (e.message === "staff-account") {
        toast.error("That's a staff console login", { description: msg || "Use your personal ResQ1 member email for the app." });
        return;
      }
      if (isSignup) {
        toast.error("Couldn't set your password", { description: formatErr(detail) || "Please try again." });
      } else if (!isSignup) {
        // A returning member may never have activated the app (they exist as a subscriber, not an account).
        // Steer them to activation instead of a dead-end sign-in / reset loop.
        if (e.response?.status === 429) {
          toast.error("Too many attempts", { description: msg || "Wait 15 minutes, then try again." });
          return;
        }
        let acct = null;
        if (v.email.includes("@")) { try { acct = await lookupByEmail(v.email.trim()); } catch (_) { /* ignore */ } }
        if (acct && acct.match && !acct.already_registered) {
          setMode("signup"); setMatched(acct); setStep("details"); setDup(null);
          toast.info("You're a ResQ1 member — set a password to activate your account");
        } else {
          toast.error("That email or password didn't match", {
            description: "Check for typos (caps or spaces), or reset your password.",
            action: {
              label: "Reset",
              onClick: async () => {
                const em = v.email.trim();
                if (!em) return toast.info("Enter your email first, then tap Reset");
                try { await forgotPassword(em); toast.success("If that email is registered, a reset link is on its way"); }
                catch (err) { toast.error("Couldn't send reset link"); }
              },
            },
          });
        }
      } else {
        toast.error("Sign-up failed", { description: formatErr(detail) });
      }
    } finally { setBusy(false); }
  };

  const toIdentify = () => { setStep("identify"); setMatched(null); setDup(null); };
  const switchMode = () => { setMode(isSignup ? "signin" : "signup"); setStep("identify"); setMatched(null); setDup(null); };
  const goSignIn = () => { if (dup?.email) setV((s) => ({ ...s, email: dup.email })); setMode("signin"); setStep("identify"); setDup(null); };
  const doReset = async () => {
    const email = dup?.email || v.email;
    if (!email) { setMode("signin"); setDup(null); return toast.info("Enter your email, then tap Forgot password"); }
    try { await forgotPassword(email); toast.success("If that email is registered, a reset link is on its way"); }
    catch (e) { toast.error("Couldn't send reset link", { description: formatErr(e.response?.data?.detail) }); }
  };

  return (
    <div style={{ padding: "40px 26px", minHeight: "100%", display: "flex", flexDirection: "column" }} data-testid="consumer-auth">
      <div style={{ marginBottom: 40 }}><Wordmark /></div>
      <Label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ width: 22, height: 1, background: C.red }} /> {isSignup ? "Create account" : "Welcome back"}
      </Label>
      <Serif size={30} style={{ marginBottom: 10 }}>
        {isSignup ? <>Set up your <em style={{ color: C.slate }}>safety net</em>.</> : <>Sign in to your <em style={{ color: C.slate }}>safety net</em>.</>}
      </Serif>
      <p style={{ color: C.slate, fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>
        {!isSignup ? "Your protection is one tap away."
          : dup ? "Good news — you're already with ResQ1."
          : step === "identify" ? "Enter your cellphone number — we'll find your existing ResQ1 cover."
          : matched ? (matched.already_registered ? "Welcome back — just set a new password to get straight in." : "We found your cover. Set a password to activate the app.")
          : "You're new to ResQ1 — let's get you set up."}
      </p>

      {/* First-time member hint on the sign-in screen */}
      {!isSignup && (
        <button onClick={switchMode} data-testid="auth-activate-hint"
          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: `${C.navy}0d`, border: `1px solid ${C.navy}22`, borderRadius: 12, padding: "12px 14px", marginBottom: 20, cursor: "pointer" }}>
          <span style={{ fontSize: 13, color: C.ink, lineHeight: 1.4 }}>
            <b>Already a ResQ1 member?</b> First time here — <span style={{ color: C.red, fontWeight: 600 }}>set your password to activate →</span>
          </span>
        </button>
      )}

      {/* SIGN IN */}
      {!isSignup && (
        <div style={{ display: "grid", gap: 16 }}>
          <Field label="Email or cell number" value={v.email} onChange={set("email")} testid="auth-email" type="text" inputMode="email" autoComplete="username" autoCapitalize="none" autoCorrect="off" placeholder="Email or cell number" />
          <PwField label="Password" value={v.password} onChange={set("password")} testid="auth-password" show={showPw} onToggle={() => setShowPw(!showPw)} autoComplete="current-password" />
        </div>
      )}

      {/* SIGN UP — STEP 1: IDENTIFY */}
      {isSignup && !dup && step === "identify" && (
        <div style={{ display: "grid", gap: 16 }}>
          {ci && (
            <div data-testid="auth-ci-banner" style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 13px", background: `${C.red}0f`, border: `1px solid ${C.red}44`, borderRadius: 10, fontSize: 13, color: C.ink }}>
              Welcome to ResQ1 — confirm your number below to activate your cover.
            </div>
          )}
          <Field label="Cellphone number" value={v.cell} onChange={set("cell")} placeholder="e.g. 0821234567" testid="auth-cell" type="tel" inputMode="tel" autoComplete="tel" />
          <button onClick={() => runLookup()} disabled={busy} data-testid="auth-lookup-continue"
            style={{ width: "100%", padding: 15, background: C.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Checking…" : "Continue →"}
          </button>
        </div>
      )}

      {/* SIGN UP — STEP 2: DETAILS */}
      {isSignup && !dup && step === "details" && (
        <div style={{ display: "grid", gap: 16 }}>
          {matched ? (
            <div data-testid="auth-match-banner" style={{ background: `${C.green}12`, border: `1px solid ${C.green}44`, borderRadius: 12, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.green, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                <ShieldCheck size={16} /> Cover found
              </div>
              <div style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{matched.masked_name}</div>
              <div style={{ fontSize: 12, color: C.slate, marginTop: 2 }}>{matched.insurer} · {matched.plan} plan</div>
              <button onClick={toIdentify} data-testid="auth-change-btn" style={{ marginTop: 8, background: "none", border: 0, color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>Not you? Use a different number</button>
            </div>
          ) : (
            <div data-testid="auth-nomatch-banner" style={{ background: `${C.amber}14`, border: `1px solid ${C.amber}44`, borderRadius: 12, padding: 12, fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>
              We couldn't find existing cover for {v.cell}. You can still create an account — family tracking is free, and you can activate the Security Package anytime.
            </div>
          )}

          {!matched && <Field label="Full name *" value={v.name} onChange={set("name")} testid="auth-name" autoComplete="name" />}
          <div>
            <Field label={ci && matched ? "Email (optional)" : "Email"} value={v.email} onChange={set("email")} testid="auth-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" />
            {ci && matched && (
              <div data-testid="auth-email-optional-hint" style={{ fontSize: 11, color: C.slate, marginTop: 5, lineHeight: 1.4 }}>
                No email of your own? Leave this blank — you'll sign in with your cell number. Don't use a family member's email.
              </div>
            )}
          </div>
          <PwField label={matched?.already_registered ? "New password" : "Create password"} value={v.password} onChange={set("password")} testid="auth-password" show={showPw} onToggle={() => setShowPw(!showPw)} autoComplete="new-password" hint="At least 6 characters" />
          {!matched && !inviteToken && (
            <div>
              <Field label="Main member's cell (dependants only)" value={v.main_member_cell} onChange={set("main_member_cell")} placeholder="Their cell, e.g. 0821234567" testid="auth-main-cell" type="tel" inputMode="tel" />
              <div style={{ fontSize: 11, color: C.slate, marginTop: 5, lineHeight: 1.4 }}>On a family plan? Enter your main member's number to link to their cover.</div>
            </div>
          )}

          <button onClick={submit} disabled={busy} data-testid="auth-submit"
            style={{ marginTop: 6, width: "100%", padding: 15, background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Please wait…" : matched ? (matched.already_registered ? "Update password & sign in →" : "Activate my cover →") : "Create account →"}
          </button>
          <div style={{ fontSize: 11, color: C.slate, textAlign: "center", lineHeight: 1.5 }}>
            By continuing you agree to our{" "}
            <a href="/terms" target="_blank" rel="noreferrer" style={{ color: C.slate, textDecoration: "underline" }}>Terms</a> and{" "}
            <a href="/popia" target="_blank" rel="noreferrer" style={{ color: C.slate, textDecoration: "underline" }}>POPIA statement</a>.
          </div>
        </div>
      )}

      {/* SIGN UP — DUPLICATE-ACCOUNT GRACE */}
      {isSignup && dup && (
        <div data-testid="auth-dup-grace" style={{ display: "grid", gap: 14 }}>
          <div style={{ background: `${C.navy}0d`, border: `1px solid ${C.navy}33`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 6 }}>Looks like you already have an account</div>
            <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.5 }}>
              {dup.email ? <>An account already exists for <b style={{ color: C.ink }}>{dup.email}</b>.</> : <>An account already exists for this number.</>} You can sign in or reset your password.
            </div>
          </div>
          <button onClick={goSignIn} data-testid="auth-dup-signin"
            style={{ width: "100%", padding: 15, background: C.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
            Sign in →
          </button>
          <button onClick={doReset} data-testid="auth-dup-reset"
            style={{ width: "100%", padding: 13, background: "transparent", color: C.red, border: `1.5px solid ${C.red}55`, borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Reset my password
          </button>
          <button onClick={toIdentify} data-testid="auth-dup-different"
            style={{ background: "none", border: 0, color: C.slate, fontSize: 12, fontWeight: 600, cursor: "pointer", alignSelf: "center" }}>
            Use a different number
          </button>
        </div>
      )}

      {/* SIGN IN submit + forgot */}
      {!isSignup && (
        <>
          <button onClick={submit} disabled={busy} data-testid="auth-submit"
            style={{ marginTop: 26, width: "100%", padding: 15, background: C.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Please wait…" : "Sign in →"}
          </button>
          <button onClick={async () => {
            if (!v.email) return toast.error("Enter your email first, then tap Forgot password");
            const em = v.email.trim();
            let acct = null;
            try { acct = await lookupByEmail(em); } catch (_) { /* ignore */ }
            if (acct && acct.match && !acct.already_registered) {
              setMode("signup"); setMatched(acct); setStep("details"); setDup(null);
              return toast.info("You're a ResQ1 member — set a password to activate your account");
            }
            try { await forgotPassword(em); toast.success("If that email is registered, a reset link is on its way"); }
            catch (e) { toast.error("Couldn't send reset link", { description: formatErr(e.response?.data?.detail) }); }
          }} data-testid="auth-forgot"
            style={{ marginTop: 14, background: "none", border: 0, color: C.red, fontSize: 13, fontWeight: 600, cursor: "pointer", alignSelf: "center" }}>
            Forgot password?
          </button>
        </>
      )}

      <div style={{ marginTop: "auto", paddingTop: 30, textAlign: "center", fontSize: 13, color: C.slate }}>
        {isSignup ? "Already protected? " : "New to ResQ1? "}
        <button onClick={switchMode} data-testid="auth-toggle" style={{ background: "none", border: 0, color: C.red, fontWeight: 600, cursor: "pointer" }}>
          {isSignup ? "Sign in" : "Create an account"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, testid, inputMode, autoComplete, autoCapitalize, autoCorrect }) {
  return (
    <div>
      <Label style={{ marginBottom: 6 }}>{label}</Label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} inputMode={inputMode} autoComplete={autoComplete}
        autoCapitalize={autoCapitalize} autoCorrect={autoCorrect} spellCheck={autoCapitalize === "none" ? false : undefined}
        style={{ width: "100%", padding: "12px 14px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 14, outline: "none", background: "#fff", color: C.ink }} />
    </div>
  );
}

function PwField({ label, value, onChange, testid, show, onToggle, autoComplete, hint }) {
  return (
    <div>
      <Label style={{ marginBottom: 6 }}>{label}</Label>
      <div style={{ position: "relative" }}>
        <input type={show ? "text" : "password"} value={value} onChange={onChange} data-testid={testid} autoComplete={autoComplete}
          style={{ width: "100%", padding: "12px 44px 12px 14px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 14, outline: "none", background: "#fff", color: C.ink }} />
        <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`} aria-label={show ? "Hide password" : "Show password"}
          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: 0, color: C.slate, cursor: "pointer", padding: 6, display: "flex" }}>
          {show ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </div>
      {hint && <div style={{ fontSize: 11, color: C.slate, marginTop: 5 }}>{hint}</div>}
    </div>
  );
}
